/**
 * Coming Soon Page
 *
 * Temporary landing page for registration while signups are disabled.
 * Allows users to subscribe to the Beehiiv mailing list for launch notifications.
 * Links to the public reef keeping tools page.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  Button,
  Alert,
  Anchor,
  Stack,
  Center,
  Box,
} from '@mantine/core';
import { IconMail, IconCheck, IconTools } from '@tabler/icons-react';
import { trackEvent } from '@/utils/analytics';

export const ComingSoonPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      trackEvent('generate_lead', {
        item_name: 'email_signup',
        item_category: 'coming_soon',
      });

      const response = await fetch('/api/public/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: 'website',
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Subscription failed');
      }

      setSubmitted(true);
      trackEvent('sign_up', {
        method: 'email',
        item_category: 'coming_soon',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maw={{ base: '100%', xs: 480 }} px={{ base: 'md', xs: 0 }} my={40}>
      <Center>
        <Stack gap="md" w="100%">
          <Title ta="center">Coming Soon</Title>
          <Text c="dimmed" size="sm" ta="center">
            Reef Data Hub is launching later this year
          </Text>

          <Paper withBorder p={30} mt={30} radius="md">
            <Stack gap="lg">
              <Text ta="center">
                Track your reef tank parameters, ICP results, dosing schedules, and more.
                Sign up to get notified when we launch.
              </Text>

              {!submitted ? (
                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <TextInput
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      leftSection={<IconMail size={16} />}
                      error={error}
                      size="md"
                      aria-label="Email address for launch notification"
                    />
                    <Button
                      type="submit"
                      fullWidth
                      loading={isSubmitting}
                      size="md"
                    >
                      Notify Me When You Launch
                    </Button>
                  </Stack>
                </form>
              ) : (
                <Alert icon={<IconCheck size={16} />} color="green" title="You're on the list!">
                  Thanks for signing up. We'll let you know when Reef Data Hub launches.
                </Alert>
              )}

              <Text size="sm" ta="center" c="dimmed">
                Already have an account?{' '}
                <Anchor component={Link} to="/login" size="sm">
                  Sign in
                </Anchor>
              </Text>
            </Stack>
          </Paper>

          {/* Link to free reef keeping tools */}
          <Paper withBorder p="md" radius="md" mt="md">
            <Stack gap="xs" align="center">
              <Box
                style={{
                  background: 'linear-gradient(135deg, var(--mantine-color-blue-6) 0%, var(--mantine-color-cyan-6) 100%)',
                  borderRadius: '50%',
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconTools size={24} color="white" />
              </Box>
              <Text fw={500} ta="center">
                Try our free reef keeping tools
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Trace Element Calculator, HTTM Scheduler, and Dose Calculator. No signup required.
              </Text>
              <Button
                component="a"
                href="https://reefdatahub.com/tools"
                target="_blank"
                rel="noopener noreferrer"
                variant="light"
                mt="xs"
              >
                Open Tools
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Center>
    </Container>
  );
};
